import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, TrendingDown, TrendingUp, Building2, Check, XCircle, Download, CheckCircle, Clock, AlertCircle, Undo2 } from 'lucide-react';
import { edificiosApi, revisorApi } from '../../lib/api';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { usePersistentTab } from '../../hooks/usePersistentTab';

// ---------- helpers ----------
const fmt = (n) => {
  if (n === null || n === undefined) return '—';
  try { return `$ ${Number(n).toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`; }
  catch { return String(n); }
};
const fmtPct = (n) => {
  if (n === null || n === undefined) return '—';
  const sign = n > 0 ? '+' : '';
  return `${sign}${n}%`;
};
const fmtDate = (iso) => {
  if (!iso) return '—';
  try { const s = String(iso); const d = new Date(s.length === 10 ? `${s}T12:00:00` : s); return isNaN(d.getTime()) ? iso : d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' }); }
  catch { return iso; }
};

const pctDelta = (curr, prev) => {
  if (prev === null || prev === undefined || prev === 0) return null;
  return ((curr - prev) / prev) * 100;
};

// Colorea celda según reglas:
// - ingresos: verde si sube, rojo si baja (cualquier %)
// - egresos: rojo si sube >15%, verde si baja >15%
const cellColor = (delta, mode) => {
  if (delta === null) return '';
  if (mode === 'ingresos') {
    if (delta > 0) return 'bg-green-50 text-green-800';
    if (delta < 0) return 'bg-red-50 text-red-800';
    return '';
  }
  // egresos
  if (delta > 15) return 'bg-red-50 text-red-800';
  if (delta < -15) return 'bg-green-50 text-green-800';
  return '';
};

const LABELS_CONCEPTO = {
  ingresos_totales: 'Ingresos totales',
  total_egresos: 'Total egresos',
  iibb: 'IIBB',
  retenciones: 'Retenciones',
  edemsa: 'Edemsa',
  tasa_municipal: 'Municipalidad',
  agua: 'Agua',
  gastos_negocio: 'Gastos de negocio',
};

// ==================== TAB COMPARATIVA ====================
const TabComparativa = ({ edificioId }) => {
  const [concepto, setConcepto] = useState('ingresos_totales');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await revisorApi.comparativa(edificioId, concepto, 3);
        setData(res.data);
      } catch { toast.error('Error al cargar comparativa'); }
      finally { setLoading(false); }
    })();
  }, [edificioId, concepto]);

  if (loading) return <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;
  if (!data) return null;

  const mode = concepto === 'ingresos_totales' ? 'ingresos' : 'egresos';
  const varPct = data.variacion_pct;
  const varColor = mode === 'ingresos'
    ? (varPct > 0 ? 'text-green-700' : varPct < 0 ? 'text-red-700' : 'text-slate-600')
    : (varPct > 15 ? 'text-red-700' : varPct < -15 ? 'text-green-700' : 'text-slate-600');

  return (
    <div className="space-y-4">
      {/* Selector */}
      <div className="flex items-center gap-3 flex-wrap">
        <label className="text-sm text-slate-600">Concepto:</label>
        <Select value={concepto} onValueChange={setConcepto}>
          <SelectTrigger className="w-64" data-testid="comparativa-concepto">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {data.conceptos_disponibles.map((c) => (
              <SelectItem key={c} value={c}>{LABELS_CONCEPTO[c] || c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Cards resumen */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <p className="text-xs uppercase text-slate-500">Total mes actual</p>
          <p className="text-xl font-semibold text-slate-900 mt-1">{fmt(data.total_mes_actual)}</p>
          <p className="text-xs text-slate-400 mt-1">{data.periodo_actual}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <p className="text-xs uppercase text-slate-500">Mes anterior</p>
          <p className="text-xl font-semibold text-slate-900 mt-1">{fmt(data.total_mes_anterior)}</p>
          <p className="text-xs text-slate-400 mt-1">{data.periodo_anterior || '—'}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <p className="text-xs uppercase text-slate-500">Diferencia %</p>
          <div className="flex items-center gap-2 mt-1">
            {varPct != null && (varPct > 0 ? <TrendingUp className="w-5 h-5" /> : <TrendingDown className="w-5 h-5" />)}
            <p className={`text-xl font-semibold ${varColor}`}>{fmtPct(varPct)}</p>
          </div>
        </div>
      </div>

      {/* Tabla */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[800px]">
          <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase text-slate-500">
            <tr>
              <th className="py-2.5 px-3 text-left font-medium sticky left-0 bg-slate-50">Unidad</th>
              <th className="py-2.5 px-3 text-left font-medium">Inquilino</th>
              {data.periodos.map((p) => (
                <th key={p} className="py-2.5 px-3 text-right font-medium">{p}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data.filas.map((f) => (
              <tr key={f.unidad_id} className="hover:bg-slate-50/60">
                <td className="py-2 px-3 font-medium text-slate-900 sticky left-0 bg-white">{f.unidad_nombre}</td>
                <td className="py-2 px-3 text-slate-700">{f.inquilino}</td>
                {data.periodos.map((p, i) => {
                  const val = f.valores[p];
                  const prev = i === 0 ? null : f.valores[data.periodos[i - 1]];
                  const delta = pctDelta(val, prev);
                  const bg = i === 0 ? '' : cellColor(delta, mode);
                  return (
                    <td
                      key={p}
                      className={`py-2 px-3 text-right font-mono ${bg}`}
                      title={delta !== null ? `vs ${data.periodos[i - 1]}: ${fmtPct(Math.round(delta * 100) / 100)}` : undefined}
                      data-testid={`comp-${f.unidad_id}-${p}`}
                    >
                      {fmt(val)}
                    </td>
                  );
                })}
              </tr>
            ))}
            <tr className="bg-slate-100 font-semibold border-t-2 border-slate-300">
              <td className="py-3 px-3 sticky left-0 bg-slate-100">Total</td>
              <td className="py-3 px-3"></td>
              {data.periodos.map((p) => (
                <td key={p} className="py-3 px-3 text-right font-mono">{fmt(data.totales_por_periodo[p])}</td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ==================== TAB FACTURACION + COBRO ====================
const TabFacturacionCobro = ({ edificioId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try { const res = await revisorApi.facturacionCobro(edificioId, 3); setData(res.data); }
      catch { toast.error('Error al cargar'); }
      finally { setLoading(false); }
    })();
  }, [edificioId]);

  if (loading) return <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;
  if (!data) return null;

  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
      <table className="w-full text-sm min-w-[1000px]">
        <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase text-slate-500">
          <tr>
            <th className="py-2.5 px-3 text-left font-medium">Unidad</th>
            <th className="py-2.5 px-3 text-left font-medium">Inquilino</th>
            <th className="py-2.5 px-3 text-left font-medium">Última actualización</th>
            {data.periodos.map((p) => (
              <th key={p} className="py-2.5 px-3 text-right font-medium">{p}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {data.filas.length === 0 ? (
            <tr><td colSpan={3 + data.periodos.length} className="py-10 text-center text-slate-400">Sin unidades en administración</td></tr>
          ) : data.filas.map((f) => (
            <tr key={f.unidad_id} className="hover:bg-slate-50/60">
              <td className="py-2 px-3 font-medium text-slate-900">{f.unidad_nombre}</td>
              <td className="py-2 px-3 text-slate-700">{f.inquilino}</td>
              <td className="py-2 px-3 text-slate-700 cursor-help" title={f.condiciones}>
                {fmtDate(f.fecha_ultima_actualizacion)}
              </td>
              {data.periodos.map((p) => {
                const c = f.valores[p] || {};
                const isCobrada = c.estado === 'cobrada';
                const cls = isCobrada ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800';
                return (
                  <td key={p} className="py-2 px-3 text-right">
                    {c.monto != null && c.monto > 0 ? (
                      <div className={`inline-flex flex-col items-end px-2 py-1 rounded ${cls}`} data-testid={`fc-${f.unidad_id}-${p}`}>
                        <span className="font-mono font-medium">{fmt(c.monto)}</span>
                        <span className="text-[10px] uppercase">{isCobrada ? 'Cobrada' : 'Pendiente'}</span>
                      </div>
                    ) : <span className="text-slate-300">—</span>}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ==================== TAB RENTA ====================
const MatrizMonto = ({ titulo, filas, periodos, totales, colorMode, formatValor }) => (
  <div className="space-y-2">
    <h3 className="font-semibold text-slate-800">{titulo}</h3>
    <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
      <table className="w-full text-sm min-w-[700px]">
        <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase text-slate-500">
          <tr>
            <th className="py-2.5 px-3 text-left font-medium">Unidad</th>
            <th className="py-2.5 px-3 text-left font-medium">Inquilino</th>
            {periodos.map((p) => (
              <th key={p} className="py-2.5 px-3 text-right font-medium">{p}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {filas.map((f) => (
            <tr key={f.unidad_id} className="hover:bg-slate-50/60">
              <td className="py-2 px-3 font-medium text-slate-900">{f.unidad_nombre}</td>
              <td className="py-2 px-3 text-slate-700">{f.inquilino}</td>
              {periodos.map((p, i) => {
                const val = f.valores[p];
                const prev = i === 0 ? null : f.valores[periodos[i - 1]];
                const delta = pctDelta(val, prev);
                // renta: rojo si cae >10%
                let bg = '';
                if (i > 0 && colorMode === 'renta' && delta !== null && delta < -10) bg = 'bg-red-50 text-red-800';
                return (
                  <td
                    key={p}
                    className={`py-2 px-3 text-right font-mono ${bg}`}
                    title={delta !== null ? `vs ${periodos[i - 1]}: ${fmtPct(Math.round(delta * 100) / 100)}` : undefined}
                  >
                    {formatValor(val)}
                  </td>
                );
              })}
            </tr>
          ))}
          {totales && (
            <tr className="bg-slate-100 font-semibold border-t-2 border-slate-300">
              <td className="py-3 px-3">Total</td>
              <td className="py-3 px-3"></td>
              {periodos.map((p) => (
                <td key={p} className="py-3 px-3 text-right font-mono">{formatValor(totales[p])}</td>
              ))}
            </tr>
          )}
        </tbody>
      </table>
    </div>
  </div>
);

const TabRenta = ({ edificioId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try { const res = await revisorApi.renta(edificioId, 4); setData(res.data); }
      catch { toast.error('Error al cargar renta'); }
      finally { setLoading(false); }
    })();
  }, [edificioId]);

  if (loading) return <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;
  if (!data) return null;

  return (
    <div className="space-y-6">
      <MatrizMonto
        titulo="Renta neta (ingresos − egresos − comisión admin)"
        filas={data.rentas.filas}
        periodos={data.periodos}
        totales={data.rentas.totales_por_periodo}
        colorMode="renta"
        formatValor={fmt}
      />
      <MatrizMonto
        titulo="Rentabilidad anual proyectada (%)"
        filas={data.rentabilidades.filas}
        periodos={data.periodos}
        totales={null}
        colorMode="renta"
        formatValor={(v) => v == null ? '—' : `${v.toFixed(2)}%`}
      />
    </div>
  );
};

// ==================== TAB COMISIONES ====================
const TabComisionesRevisor = ({ edificioId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try { const res = await revisorApi.comisiones(edificioId, 4); setData(res.data); }
      catch { toast.error('Error al cargar comisiones'); }
      finally { setLoading(false); }
    })();
  }, [edificioId]);

  if (loading) return <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;
  if (!data) return null;

  return (
    <div className="space-y-3">
      <div className="text-xs text-slate-500">
        Comisión de gestión = <b>{data.porcentaje_gestion}%</b> del total de comisión de administración.
      </div>
      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[800px]">
          <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase text-slate-500">
            <tr>
              <th className="py-2.5 px-3 text-left font-medium">Unidad</th>
              <th className="py-2.5 px-3 text-left font-medium">Inquilino</th>
              <th className="py-2.5 px-3 text-center font-medium">% Admin</th>
              {data.periodos.map((p) => (
                <th key={p} className="py-2.5 px-3 text-right font-medium">{p}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data.filas.map((f) => (
              <tr key={f.unidad_id} className="hover:bg-slate-50/60">
                <td className="py-2 px-3 font-medium text-slate-900">{f.unidad_nombre}</td>
                <td className="py-2 px-3 text-slate-700">{f.inquilino}</td>
                <td className="py-2 px-3 text-center text-slate-600">{f.porcentaje_admin}%</td>
                {data.periodos.map((p) => (
                  <td key={p} className="py-2 px-3 text-right font-mono">{fmt(f.valores[p])}</td>
                ))}
              </tr>
            ))}
            {/* Total administración */}
            <tr className="bg-slate-100 font-semibold border-t-2 border-slate-300">
              <td className="py-3 px-3" colSpan={3}>Total comisión administración</td>
              {data.periodos.map((p, i) => {
                const variacion = data.variaciones_admin[p];
                return (
                  <td key={p} className="py-3 px-3 text-right font-mono">
                    {fmt(data.totales_administracion[p])}
                    {variacion != null && (
                      <div className={`text-[10px] font-normal ${variacion > 0 ? 'text-green-700' : variacion < 0 ? 'text-red-700' : 'text-slate-400'}`}>
                        {fmtPct(variacion)}
                      </div>
                    )}
                  </td>
                );
              })}
            </tr>
            {/* Comisión gestión */}
            <tr className="bg-amber-50 font-medium">
              <td className="py-3 px-3" colSpan={3}>Comisión de gestión ({data.porcentaje_gestion}%)</td>
              {data.periodos.map((p) => (
                <td key={p} className="py-3 px-3 text-right font-mono">{fmt(data.totales_gestion[p])}</td>
              ))}
            </tr>
            {/* Resultado neto */}
            <tr className="bg-green-50 font-semibold">
              <td className="py-3 px-3" colSpan={3}>Resultado neto global</td>
              {data.periodos.map((p) => (
                <td key={p} className="py-3 px-3 text-right font-mono">{fmt(data.resultado_neto_global[p])}</td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ==================== TAB PAGO RENTA ====================
const RevisionBadge = ({ estado, motivo }) => {
  if (estado === 'aprobado') {
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700" title="Aprobado por revisor"><CheckCircle className="w-3 h-3" /> Aprobado</span>;
  }
  if (estado === 'bloqueado') {
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-red-100 text-red-700" title={motivo || 'Bloqueado'}><AlertCircle className="w-3 h-3" /> NO pagar</span>;
  }
  return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-slate-100 text-slate-500"><Clock className="w-3 h-3" /> Sin revisar</span>;
};

const PagoEstadoBadge = ({ estado, fecha }) => {
  if (estado === 'pagado') {
    return (
      <div className="inline-flex flex-col items-start">
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700"><CheckCircle className="w-3 h-3" /> Pagado</span>
        {fecha && <span className="text-[10px] text-slate-500 mt-0.5">{fmtDate(fecha)}</span>}
      </div>
    );
  }
  if (estado === 'pendiente') {
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-amber-100 text-amber-700"><Clock className="w-3 h-3" /> Pendiente</span>;
  }
  return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-slate-100 text-slate-500">Falta factura</span>;
};

const PagoRentaRow = ({ fila, edificioId, onChanged, readOnly }) => {
  const [loadingAccion, setLoadingAccion] = useState(null);
  const [bloqueoOpen, setBloqueoOpen] = useState(null); // {propietario_id}
  const [motivo, setMotivo] = useState('');

  const handleAccion = async (propId, accion, motivoArg = null) => {
    const payload = { accion, periodo: filaPeriodo, propietario_id: propId || null, motivo: motivoArg };
    setLoadingAccion(`${propId}-${accion}`);
    try {
      await revisorApi.pagoRentaAccion(edificioId, fila.unidad_id, payload);
      toast.success(
        accion === 'aprobar' ? 'Pago aprobado — se notificará al analista' :
        accion === 'bloquear' ? 'Pago bloqueado — se notificará al analista' :
        'Revisión deshecha'
      );
      onChanged && onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally {
      setLoadingAccion(null);
      setBloqueoOpen(null);
      setMotivo('');
    }
  };

  // Período desde contexto parent
  const filaPeriodo = fila._periodo;

  return fila.propietarios.map((p, idx) => {
    const canAprobar = fila.alquiler_cobrado && fila.factura_propietario_subida && p.revision_estado !== 'aprobado';
    const puedeActuar = !readOnly && (p.revision_estado === 'sin_revisar' || p.revision_estado === 'bloqueado' || p.revision_estado === 'aprobado');
    return (
      <tr key={`${fila.unidad_id}-${p.propietario_id || idx}`} className="hover:bg-slate-50/60">
        {idx === 0 && (
          <>
            <td rowSpan={fila.propietarios.length} className="py-2 px-3 font-medium text-slate-900 align-top">{fila.unidad_nombre}</td>
            <td rowSpan={fila.propietarios.length} className="py-2 px-3 text-slate-700 align-top">{fila.inquilino}</td>
          </>
        )}
        <td className="py-2 px-3 text-slate-700">{p.propietario_nombre}{fila.propietarios.length > 1 ? ` · ${p.porcentaje}%` : ''}</td>
        <td className="py-2 px-3 text-right font-mono">{fmt(p.monto)}</td>
        {idx === 0 && (
          <td rowSpan={fila.propietarios.length} className="py-2 px-3 text-center align-top">
            {fila.alquiler_cobrado ? (
              <span className="inline-flex items-center gap-1 text-xs text-green-700"><CheckCircle className="w-3.5 h-3.5" /> Cobrado</span>
            ) : (
              <span className="inline-flex items-center gap-1 text-xs text-slate-500"><Clock className="w-3.5 h-3.5" /> Pendiente</span>
            )}
            {fila.fecha_cobro_alquiler && <div className="text-[10px] text-slate-400 mt-0.5">{fmtDate(fila.fecha_cobro_alquiler)}</div>}
          </td>
        )}
        {idx === 0 && (
          <td rowSpan={fila.propietarios.length} className="py-2 px-3 text-center align-top">
            {fila.factura_propietario_subida ? (
              <span className="inline-flex items-center gap-1 text-xs text-green-700"><CheckCircle className="w-3.5 h-3.5" /> Subida</span>
            ) : (
              <span className="inline-flex items-center gap-1 text-xs text-slate-500"><Clock className="w-3.5 h-3.5" /> Pendiente</span>
            )}
            {fila.fecha_factura_propietario && <div className="text-[10px] text-slate-400 mt-0.5">{fmtDate(fila.fecha_factura_propietario)}</div>}
          </td>
        )}
        <td className="py-2 px-3 text-center">
          <div className="flex flex-col items-center gap-1">
            <RevisionBadge estado={p.revision_estado} motivo={p.revision_motivo} />
            {p.revision_motivo && p.revision_estado === 'bloqueado' && (
              <span className="text-[10px] text-red-600 max-w-[160px] truncate" title={p.revision_motivo}>{p.revision_motivo}</span>
            )}
            {p.revision_by_nombre && p.revision_estado !== 'sin_revisar' && (
              <span className="text-[10px] text-slate-400">{p.revision_by_nombre}</span>
            )}
          </div>
        </td>
        {!readOnly && (
          <td className="py-2 px-3 text-center">
            <div className="inline-flex items-center gap-1">
              {p.revision_estado === 'sin_revisar' && (
                <>
                  <Button size="sm" variant="outline"
                    disabled={!canAprobar || loadingAccion !== null}
                    className="h-7 text-xs bg-green-50 border-green-200 text-green-700 hover:bg-green-100 disabled:opacity-40"
                    onClick={() => handleAccion(p.propietario_id, 'aprobar')}
                    data-testid={`aprobar-${fila.unidad_id}`}
                    title={!canAprobar ? 'Requiere alquiler cobrado y factura del propietario subida' : 'Aprobar pago'}
                  >
                    <Check className="w-3 h-3 mr-0.5" /> Pagar
                  </Button>
                  <Button size="sm" variant="outline"
                    disabled={loadingAccion !== null}
                    className="h-7 text-xs bg-red-50 border-red-200 text-red-700 hover:bg-red-100"
                    onClick={() => setBloqueoOpen({ propietario_id: p.propietario_id })}
                    data-testid={`bloquear-${fila.unidad_id}`}
                  >
                    <XCircle className="w-3 h-3 mr-0.5" /> NO pagar
                  </Button>
                </>
              )}
              {puedeActuar && p.revision_estado !== 'sin_revisar' && (
                <Button size="sm" variant="outline"
                  disabled={loadingAccion !== null}
                  className="h-7 text-xs"
                  onClick={() => handleAccion(p.propietario_id, 'deshacer')}
                  data-testid={`deshacer-${fila.unidad_id}`}
                >
                  <Undo2 className="w-3 h-3 mr-0.5" /> Deshacer
                </Button>
              )}
            </div>
          </td>
        )}
        <td className="py-2 px-3 text-center">
          <PagoEstadoBadge estado={p.estado_pago} fecha={p.fecha_pago} />
        </td>

        {bloqueoOpen?.propietario_id === p.propietario_id && (
          <Dialog open onOpenChange={(o) => { if (!o) { setBloqueoOpen(null); setMotivo(''); } }}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Bloquear pago — {fila.unidad_nombre} · {p.propietario_nombre}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <Label>Motivo (requerido)</Label>
                <textarea
                  value={motivo}
                  onChange={(e) => setMotivo(e.target.value)}
                  className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm min-h-[100px]"
                  placeholder="Ej.: esperar decisión del propietario, comprobante con error..."
                  data-testid="motivo-bloqueo"
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => { setBloqueoOpen(null); setMotivo(''); }}>Cancelar</Button>
                <Button
                  className="bg-red-600 hover:bg-red-700 text-white"
                  disabled={!motivo.trim() || loadingAccion !== null}
                  onClick={() => handleAccion(p.propietario_id, 'bloquear', motivo)}
                  data-testid="confirmar-bloqueo"
                >
                  Confirmar bloqueo
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </tr>
    );
  });
};

const PagoRentaTabla = ({ filas, edificioId, periodo, onChanged, readOnly = false }) => {
  const filasConPeriodo = filas.map(f => ({ ...f, _periodo: periodo }));
  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
      <table className="w-full text-sm min-w-[1300px]">
        <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase text-slate-500">
          <tr>
            <th className="py-2.5 px-3 text-left font-medium">Unidad</th>
            <th className="py-2.5 px-3 text-left font-medium">Inquilino</th>
            <th className="py-2.5 px-3 text-left font-medium">Propietario</th>
            <th className="py-2.5 px-3 text-right font-medium">Renta calculada</th>
            <th className="py-2.5 px-3 text-center font-medium">Alquiler mes</th>
            <th className="py-2.5 px-3 text-center font-medium">Factura propietario</th>
            <th className="py-2.5 px-3 text-center font-medium">Revisión</th>
            {!readOnly && <th className="py-2.5 px-3 text-center font-medium">Acciones</th>}
            <th className="py-2.5 px-3 text-center font-medium">Pago</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {filasConPeriodo.length === 0 ? (
            <tr><td colSpan={readOnly ? 8 : 9} className="py-10 text-center text-slate-400">Sin unidades en administración</td></tr>
          ) : filasConPeriodo.flatMap(f => (
            <PagoRentaRow key={f.unidad_id} fila={f} edificioId={edificioId} onChanged={onChanged} readOnly={readOnly} />
          ))}
        </tbody>
      </table>
    </div>
  );
};

const TabPagoRenta = ({ edificioId }) => {
  const [vista, setVista] = useState('actual'); // 'actual' | 'historial'
  const [loading, setLoading] = useState(true);
  const [actual, setActual] = useState(null);
  const [historial, setHistorial] = useState([]);

  const fetchActual = async () => {
    const res = await revisorApi.pagoRenta(edificioId);
    setActual(res.data);
  };
  const fetchHistorial = async () => {
    const res = await revisorApi.pagoRentaHistorial(edificioId, 6);
    setHistorial(res.data.historial || []);
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        if (vista === 'actual') { await fetchActual(); }
        else { await fetchHistorial(); }
      } catch { toast.error('Error al cargar pagos'); }
      finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vista, edificioId]);

  return (
    <div className="space-y-4">
      <div className="inline-flex rounded-md border border-slate-200 bg-white p-1">
        <button
          onClick={() => setVista('actual')}
          className={`px-3 py-1 text-sm rounded ${vista === 'actual' ? 'bg-indigo-600 text-white' : 'text-slate-600 hover:bg-slate-50'}`}
          data-testid="vista-actual"
        >Mes actual</button>
        <button
          onClick={() => setVista('historial')}
          className={`px-3 py-1 text-sm rounded ${vista === 'historial' ? 'bg-indigo-600 text-white' : 'text-slate-600 hover:bg-slate-50'}`}
          data-testid="vista-historial"
        >Histórico</button>
      </div>

      {loading ? (
        <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
      ) : vista === 'actual' ? (
        <PagoRentaTabla filas={actual?.filas || []} periodo={actual?.periodo} edificioId={edificioId} onChanged={fetchActual} />
      ) : (
        <div className="space-y-6">
          {historial.map((h) => (
            <div key={h.periodo}>
              <h3 className="font-semibold text-slate-800 mb-2">{h.periodo}</h3>
              <PagoRentaTabla filas={h.filas} periodo={h.periodo} edificioId={edificioId} onChanged={fetchHistorial} readOnly />
            </div>
          ))}
          {historial.length === 0 && (
            <div className="text-center py-10 text-slate-400 bg-white border border-slate-200 rounded-lg">Sin histórico</div>
          )}
        </div>
      )}
    </div>
  );
};

// ==================== MAIN ====================
export default function RevisorEdificioDetalle() {
  const { edificioId } = useParams();
  const navigate = useNavigate();
  const [edificio, setEdificio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = usePersistentTab('tab', 'comparativa');

  useEffect(() => {
    (async () => {
      try { const res = await edificiosApi.get(edificioId); setEdificio(res.data); }
      catch { toast.error('Error al cargar edificio'); }
      finally { setLoading(false); }
    })();
  }, [edificioId]);

  if (loading) {
    return <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  }

  return (
    <div className="p-4 md:p-8">
      <button
        onClick={() => navigate('/revisor/edificios')}
        className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-4"
      >
        <ArrowLeft className="w-4 h-4" /> Volver
      </button>

      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
          <Building2 className="w-5 h-5 text-indigo-700" />
        </div>
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">{edificio?.nombre}</h1>
          <p className="text-slate-500">{edificio?.direccion}</p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="comparativa" data-testid="tab-comparativa">Comparativa</TabsTrigger>
          <TabsTrigger value="facturacion-cobro" data-testid="tab-fac-cobro">Facturación y Cobro</TabsTrigger>
          <TabsTrigger value="renta" data-testid="tab-renta">Renta</TabsTrigger>
          <TabsTrigger value="pago-renta" data-testid="tab-pago-renta">Pago renta</TabsTrigger>
          <TabsTrigger value="comisiones" data-testid="tab-comisiones">Comisiones</TabsTrigger>
        </TabsList>

        <TabsContent value="comparativa" className="mt-0"><TabComparativa edificioId={edificioId} /></TabsContent>
        <TabsContent value="facturacion-cobro" className="mt-0"><TabFacturacionCobro edificioId={edificioId} /></TabsContent>
        <TabsContent value="renta" className="mt-0"><TabRenta edificioId={edificioId} /></TabsContent>
        <TabsContent value="pago-renta" className="mt-0"><TabPagoRenta edificioId={edificioId} /></TabsContent>
        <TabsContent value="comisiones" className="mt-0"><TabComisionesRevisor edificioId={edificioId} /></TabsContent>
      </Tabs>
    </div>
  );
}
